import {
  RyftEditBankAccountForm,
  type RyftBankAccountFormValues,
  type RyftBankAccountSubmissionValues,
} from "../components/RyftEditBankAccountForm";
import type { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import { Card, CardContent, H2 } from "@sellar-io/components";
import { useRef } from "react";
import { useNavigate } from "react-router";
import { Alert } from "~/components/alert/Alert";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING } from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useBusiness } from "../../../hooks/api/v2/business/useBusiness";
import { useRyftManagementPayoutMethodCreate } from "../../../hooks/api/v2/ryftManagement/useRyftManagementPayoutMethodCreate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { EmptyBusinessBoundary } from "./components/EmptyBusinessBoundary";
import { RyftNonHostedOnboardingStepRedirect } from "./components/RyftNonHostedOnboardingStepRedirect";

export const RyftNonHostedOnboardingBusinessBankAccount = () => {
  return <RyftNonHostedOnboardingBusinessBankAccountLayout />;
};

const RyftNonHostedOnboardingBusinessBankAccountLayout = () => {
  return (
    <RyftNonHostedOnboardingStepRedirect stepId="add-bank-account">
      <RyftNonHostedOnboardingBusinessBankAccountContent />
    </RyftNonHostedOnboardingStepRedirect>
  );
};

const RyftNonHostedOnboardingBusinessBankAccountContent = () => {
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const businessQuery = useBusiness(business?.id);

  return (
    <>
      <NavigationBar
        title="Add bank account"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={() =>
          navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING)
        }
      />
      <PageContainer className="mx-auto flex max-w-3xl flex-col gap-6 p-4 md:p-6">
        <EmptyBusinessBoundary
          isLoading={businessQuery.isFetching}
          error={businessQuery.isError ? businessQuery.error : undefined}
          business={businessQuery.data}
        >
          {(loadedBusiness) => (
            <RyftBankAccountContent business={loadedBusiness} />
          )}
        </EmptyBusinessBoundary>
      </PageContainer>
    </>
  );
};

const RyftBankAccountContent = ({
  business,
}: {
  business: BusinessGetResponseType;
}) => {
  const navigate = useNavigate();
  const createPayoutMethodMutation = useRyftManagementPayoutMethodCreate();
  const formContainerRef = useRef<HTMLDivElement>(null);

  const errorMessage =
    apiV2AssertError(createPayoutMethodMutation.error)?.response?.data
      .message ?? "Unable to add this bank account to your Ryft account";

  const handleAddBankAccount = async (
    values: RyftBankAccountSubmissionValues,
  ) => {
    await createPayoutMethodMutation.mutateAsync(values, {
      onSuccess: () => {
        navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING);
      },
      onError: () => {
        formContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
    });
  };

  return (
    <>
      <header ref={formContainerRef} className="flex flex-col gap-2">
        <H2>Add bank account</H2>
        <p className="max-w-2xl text-sm leading-6 text-zinc-600 md:text-base">
          Add the GBP bank account where you want Ryft to send your payouts.
        </p>
      </header>

      {createPayoutMethodMutation.isError && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}

      <Card className="flex flex-col border border-zinc-200 bg-white">
        <CardContent className="flex flex-col gap-4 p-4">
          <RyftEditBankAccountForm
            mode="create"
            initialData={getInitialFormValues(business)}
            isSubmitting={createPayoutMethodMutation.isPending}
            onSubmit={handleAddBankAccount}
          />
        </CardContent>
      </Card>
    </>
  );
};

const getInitialFormValues = (
  business: BusinessGetResponseType,
): RyftBankAccountFormValues => ({
  sortCode: "",
  accountNumber: "",
  accountHolderAddress: {
    lineOne: [business.InvoiceAddress.name, business.InvoiceAddress.street]
      .filter(Boolean)
      .join(", "),
    city: business.InvoiceAddress.city,
    country: business.InvoiceAddress.country,
    postalCode: business.InvoiceAddress.postalCode,
  },
});
